import type { DataLayerObject } from "@gtm-support/vue-gtm";
import type { PersistCookies } from "@shared/store/persist/types";
import { CONFIG } from "@shared/config";
import { trackConsentUpdated } from "./v2/consent";

const {
  application: {
    initialization: {
      tracking: { gtmContainerId, gtmContainerIdV2 },
    },
  },
} = CONFIG;

export const isValidGtmId = (): boolean => {
  const id = gtmContainerIdV2 || gtmContainerId;
  return typeof id.trim() === "string" && id.trim().startsWith("GTM-");
};

// GTM consent API specifically checks `Object.prototype.toString.call(item) === '[object Arguments]'`.
// This means we can only pass native javascript arguments directly to window.dataLayer.
// We keeping the command, action, params to have type safety on the call site.
/* eslint-disable @typescript-eslint/no-unused-vars, prefer-rest-params */
function gtag(
  command: "consent",
  action: "default" | "update",
  params: DataLayerObject,
): void {
  (window.dataLayer ??= []).push(arguments);
}
/* eslint-enable @typescript-eslint/no-unused-vars, prefer-rest-params */

const idGranted = (v: boolean): "granted" | "denied" =>
  v ? "granted" : "denied";

export const pushGtmConsentDefault = (): void => {
  if (typeof window === "undefined" || !isValidGtmId()) return;
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "denied",
    personalization_storage: "denied",
    security_storage: "granted",
    wait_for_update: 500,
  } satisfies DataLayerObject);
};

export const pushGtmConsentUpdateFromPersist = (
  cookies: PersistCookies | undefined,
): void => {
  if (typeof window === "undefined" || !isValidGtmId() || !cookies) return;
  gtag("consent", "update", {
    ad_storage: idGranted(cookies.advertising),
    ad_user_data: idGranted(cookies.advertising),
    ad_personalization: idGranted(cookies.advertising),
    analytics_storage: idGranted(cookies.analytics),
    functionality_storage: idGranted(cookies.performance),
    personalization_storage: idGranted(cookies.performance),
    security_storage: "granted",
  } satisfies DataLayerObject);

  trackConsentUpdated();
};
